'use client';
import React from 'react';

import Image from 'next/image';
import styled from 'styled-components';

import Modal from '@components/Modal';
import Markdown from '@components/utils/Markdown';
import zIndices from '@utils/theme/zIndices';
import { useBreakpoint } from '@contexts/breakpoints';

import {
  NextEventsWrapper,
  NextEventsTitle,
  NextEventsList,
  NextEventItem,
  NextEventsArtistName,
} from './styled';

interface EventInfoModalProps {
  isVisible: boolean;
  onClose: () => void;
  title?: string;
  artistName?: string;
  image?: string | null;
  dates?: string[];
  description?: string;
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(p) => p.theme.mediaQueries.LARGE_1X_UP} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ImageColumn = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  width: 100%;
  align-items: center; /* center vertically when in row layout & horizontally in column */
  ${(p) => p.theme.mediaQueries.LARGE_1X_UP} {
    width: 200px;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  padding-right: 4px;
  flex: 1;
  ${(p) => p.theme.mediaQueries.LARGE_1X_UP} {
    width: 400px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 8px 0 4px;
  color: ${(p) => p.theme.palette.darkGrey};
`;

const FullImage = styled.div`
  position: relative;
  width: 100%; /* full width on mobile */
  height: 200px;
  border-radius: 8px;
  margin: 0 auto;
  img {
    object-fit: contain; /* keep from cropping */
    object-position: center;
    background-color: transparent; /* remove black bars background */
  }
  ${(p) => p.theme.mediaQueries.LARGE_1X_UP} {
    width: 200px; /* desktop fixed size */
    height: 200px;
  }
`;

const FullDescription = styled(Markdown)`
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => p.theme.palette.brownishGrey};
`;

const EventInfoModal: React.FC<EventInfoModalProps> = ({
  onClose,
  title,
  image,
  dates,
  artistName,
  description,
}) => {
  const { xl } = useBreakpoint();
  return (
    <Modal
      isVisible={true}
      onCancel={onClose}
      padding='20px'
      backgroundColor='white'
      closeable
      title={title || 'Etkinlik Bilgileri'}
      zIndex={zIndices.modal}
      width={xl ? 720 : '100%'}
    >
      <ModalBody>
        <ImageColumn>
          <FullImage>
            <Image
              src={image || '/static/images/placeholder-image.webp'}
              alt={title || ''}
              fill
              quality={90}
              sizes='(max-width: 1199px) 100vw'
              priority
            />
          </FullImage>
        </ImageColumn>
        <ScrollContainer>
          {artistName ? (
            <NextEventsArtistName>{artistName}</NextEventsArtistName>
          ) : null}
          {dates && dates.length > 0 && (
            <NextEventsWrapper>
              <NextEventsTitle>Tarihler</NextEventsTitle>
              <NextEventsList>
                {dates.map((d) => (
                  <NextEventItem key={d}>{d}</NextEventItem>
                ))}
              </NextEventsList>
            </NextEventsWrapper>
          )}
          {description && (
            <div>
              <SectionTitle>Bilgiler</SectionTitle>
              <FullDescription text={description} />
            </div>
          )}
        </ScrollContainer>
      </ModalBody>
    </Modal>
  );
};

export default EventInfoModal;
